import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { useAuth, useReadOnly } from "../lib/auth.js";
import { formatAuPhoneForDisplay, normalizeAuPhone } from "../lib/phone.js";
import { formatDateTime, formatWeekdayDateOnly } from "../lib/datetime.js";
import { Badge, Button, EmptyState, Field, Input, Modal, Select } from "../lib/ui.js";
import { StickyTitle } from "../lib/StickyTitle.js";
import { CallDetailModal } from "./CallDetail.js";
import { JobPhotos } from "./JobPhotos.js";
import { JOB_STAGES, JOB_URGENCIES, OPEN_STAGES, formatQuote, quoteToCents, sortJobs, stageLabel, urgencyLabel, type JobStage } from "../lib/jobs.js";

export interface JobRow {
  id: string;
  customer_id: string | null;
  call_id: string | null;
  source: string;
  stage: JobStage;
  lost_reason: string | null;
  title: string;
  description: string | null;
  urgency: "emergency" | "urgent" | "routine";
  contact_name: string | null;
  contact_phone: string | null;
  address_line: string | null;
  suburb: string | null;
  postcode: string | null;
  distance_km: number | null;
  service_area_status: "in" | "review" | "out" | null;
  quote_amount_cents: number | null;
  quoted_on: string | null;
  created_at: string;
  inspection_booking_id: string | null;
  scheduled_start: string | null;
  duration_days: number | null;
  scheduled_end: string | null;
  crew_resource_id: string | null;
}

interface CrewOption {
  id: string;
  name: string;
}

interface Conflict {
  type: "booking" | "job";
  starts_at: string;
  ends_at: string;
  what: string | null;
  who: string | null;
}

interface NoteRow {
  id: string;
  kind: "note" | "event";
  body: string;
  created_at: string;
}

const COLUMNS =
  "id, customer_id, call_id, source, stage, lost_reason, title, description, urgency, contact_name, contact_phone, address_line, suburb, postcode, distance_km, service_area_status, quote_amount_cents, quoted_on, created_at, inspection_booking_id, scheduled_start, duration_days, scheduled_end, crew_resource_id";

function jobErrorMessage(message: string): string {
  if (message.includes("job_title_invalid")) return "Give the job a short title.";
  if (message.includes("job_urgency_invalid")) return "Choose how urgent it is.";
  if (message.includes("job_postcode_invalid")) return "A postcode is four digits.";
  if (message.includes("job_quote_invalid")) return "Enter the quote as a dollar amount.";
  if (message.includes("job_start_not_open")) return "The business is closed on that day. Pick a day it is open.";
  if (message.includes("no_business_hours")) return "Set your opening hours in Business settings first, so the job days can follow them.";
  if (message.includes("job_days_invalid")) return "Enter a number of days between 1 and 60.";
  if (message.includes("job_crew_invalid")) return "Choose a crew.";
  if (message.includes("job_not_schedulable")) return "A finished or lost job cannot be scheduled. Change its stage first.";
  if (message.includes("jobs_not_enabled")) return "Leads and jobs is switched off for this business (Business settings).";
  if (message.includes("not_authorized")) return "You do not have permission to change jobs.";
  return message || "That could not be saved.";
}

function urgencyTone(urgency: string): "danger" | "warning" | "neutral" {
  return urgency === "emergency" ? "danger" : urgency === "urgent" ? "warning" : "neutral";
}

function AreaBadge({ job }: { job: Pick<JobRow, "service_area_status" | "distance_km"> }) {
  if (!job.service_area_status) return null;
  const km = job.distance_km !== null ? ` ${job.distance_km} km` : "";
  if (job.service_area_status === "in") return <Badge tone="success">{`In area${km}`}</Badge>;
  if (job.service_area_status === "review") return <Badge tone="warning">{`Edge of area${km}`}</Badge>;
  return <Badge tone="danger">{`Out of area${km}`}</Badge>;
}

interface Draft {
  title: string;
  description: string;
  urgency: string;
  contactName: string;
  contactPhone: string;
  addressLine: string;
  suburb: string;
  postcode: string;
  quote: string;
}

function draftOf(job: JobRow | null): Draft {
  return {
    title: job?.title ?? "",
    description: job?.description ?? "",
    urgency: job?.urgency ?? "routine",
    contactName: job?.contact_name ?? "",
    contactPhone: job?.contact_phone ? formatAuPhoneForDisplay(job.contact_phone) : "",
    addressLine: job?.address_line ?? "",
    suburb: job?.suburb ?? "",
    postcode: job?.postcode ?? "",
    quote: job?.quote_amount_cents != null ? String(job.quote_amount_cents / 100) : "",
  };
}

/** The phone number a draft holds as E.164, "" when blank, or an error message. */
function draftPhone(draft: Draft): { ok: true; e164: string } | { ok: false; error: string } {
  if (!draft.contactPhone.trim()) return { ok: true, e164: "" };
  const n = normalizeAuPhone(draft.contactPhone, null);
  return n.ok ? { ok: true, e164: n.e164 } : { ok: false, error: n.error };
}

function JobFields({ draft, setDraft, disabled, showQuote }: { draft: Draft; setDraft: (d: Draft) => void; disabled: boolean; showQuote: boolean }) {
  const set = (key: keyof Draft) => (e: { target: { value: string } }) => setDraft({ ...draft, [key]: e.target.value });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Field label="Title">
        <Input value={draft.title} maxLength={200} disabled={disabled} onChange={set("title")} />
      </Field>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Field label="Urgency">
          <Select value={draft.urgency} disabled={disabled} onChange={set("urgency")} style={{ width: 150 }}>
            {JOB_URGENCIES.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Contact name">
          <Input value={draft.contactName} maxLength={100} disabled={disabled} onChange={set("contactName")} style={{ width: 200 }} />
        </Field>
        <Field label="Contact phone">
          <Input value={draft.contactPhone} maxLength={30} disabled={disabled} onChange={set("contactPhone")} style={{ width: 170 }} />
        </Field>
      </div>
      <Field label="Street address">
        <Input value={draft.addressLine} maxLength={200} disabled={disabled} onChange={set("addressLine")} />
      </Field>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Field label="Suburb">
          <Input value={draft.suburb} maxLength={100} disabled={disabled} onChange={set("suburb")} style={{ width: 200 }} />
        </Field>
        <Field label="Postcode">
          <Input value={draft.postcode} maxLength={4} inputMode="numeric" disabled={disabled} onChange={set("postcode")} style={{ width: 90 }} />
        </Field>
        {showQuote && (
          <Field label="Quote ($)">
            <Input value={draft.quote} maxLength={12} inputMode="decimal" disabled={disabled} onChange={set("quote")} style={{ width: 120 }} />
          </Field>
        )}
      </div>
      <Field label="What they need">
        <textarea
          className="rc-field"
          rows={4}
          maxLength={4000}
          value={draft.description}
          disabled={disabled}
          onChange={set("description")}
          style={{ resize: "vertical", fontFamily: "inherit" }}
        />
      </Field>
    </div>
  );
}

function AddJobModal({ businessId, onClose, onSaved }: { businessId: string; onClose: () => void; onSaved: () => void }) {
  const [draft, setDraft] = useState<Draft>(draftOf(null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!draft.title.trim()) return setError("Give the job a short title.");
    const phone = draftPhone(draft);
    if (!phone.ok) return setError(phone.error);
    setBusy(true);
    const { error: err } = await supabase.rpc("rpc_portal_create_job", {
      p_business_id: businessId,
      p_title: draft.title,
      p_description: draft.description,
      p_urgency: draft.urgency,
      p_contact_name: draft.contactName,
      p_contact_phone: phone.e164,
      p_address_line: draft.addressLine,
      p_suburb: draft.suburb,
      p_postcode: draft.postcode,
      p_custom: {},
    });
    setBusy(false);
    if (err) return setError(jobErrorMessage(err.message));
    onSaved();
    onClose();
  }

  return (
    <Modal title="Add a job" onClose={onClose} width={620}>
      <form onSubmit={submit}>
        <JobFields draft={draft} setDraft={setDraft} disabled={busy} showQuote={false} />
        {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "10px 0 0" }}>{error}</p>}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Adding..." : "Add job"}
          </Button>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** The job's dates, crew and calendar: schedules it over open days, warns about bookings in the way, and takes it off the calendar. */
function ScheduleSection({ job, readOnly, businessId, onChanged }: { job: JobRow; readOnly: boolean; businessId: string; onChanged: () => void }) {
  const [crews, setCrews] = useState<CrewOption[]>([]);
  const [start, setStart] = useState(job.scheduled_start ?? "");
  const [days, setDays] = useState(job.duration_days ? String(job.duration_days) : "1");
  const [crew, setCrew] = useState(job.crew_resource_id ?? "");
  const [conflicts, setConflicts] = useState<Conflict[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inspection, setInspection] = useState<string | null>(null);

  useEffect(() => {
    void supabase
      .from("resources")
      .select("id, name")
      .eq("business_id", businessId)
      .eq("active", true)
      .order("name")
      .then(({ data }: { data: CrewOption[] | null }) => {
        const list = data ?? [];
        setCrews(list);
        setCrew((current) => current || list[0]?.id || "");
      });
  }, [businessId]);

  useEffect(() => {
    if (!job.inspection_booking_id) return;
    void supabase
      .from("booking_items")
      .select("starts_at, cancelled_at, services(name)")
      .eq("booking_id", job.inspection_booking_id)
      .order("starts_at")
      .then(({ data }) => {
        const items = (data ?? []) as unknown as Array<{ starts_at: string; cancelled_at: string | null; services: { name: string } | null }>;
        const live = items.find((i) => !i.cancelled_at);
        setInspection(live ? `${live.services?.name ?? "Inspection"}: ${formatDateTime(live.starts_at)}` : "The inspection booking was cancelled.");
      });
  }, [job.inspection_booking_id]);

  async function schedule(force: boolean) {
    setError(null);
    const n = Number.parseInt(days, 10);
    if (!start) return setError("Pick a start day.");
    if (!(n >= 1 && n <= 60)) return setError("Enter a number of days between 1 and 60.");
    if (!crew) return setError("Choose a crew.");
    setBusy(true);
    const { data, error: err } = await supabase.rpc("rpc_portal_schedule_job", { p_job_id: job.id, p_start: start, p_days: n, p_crew: crew, p_force: force });
    setBusy(false);
    if (err) return setError(jobErrorMessage(err.message));
    const result = data as { ok: boolean; conflicts: Conflict[] } | null;
    if (result && !result.ok) {
      setConflicts(result.conflicts);
      return;
    }
    setConflicts(null);
    onChanged();
  }

  async function unschedule() {
    setError(null);
    setBusy(true);
    const { error: err } = await supabase.rpc("rpc_portal_unschedule_job", { p_job_id: job.id });
    setBusy(false);
    if (err) return setError(jobErrorMessage(err.message));
    setConflicts(null);
    onChanged();
  }

  const crewName = crews.find((c) => c.id === job.crew_resource_id)?.name;
  return (
    <div style={{ borderTop: "1px solid var(--rc-border)", marginTop: 18, paddingTop: 14 }}>
      <h3 style={{ fontSize: 14, margin: "0 0 8px" }}>Schedule</h3>
      {inspection && <p style={{ fontSize: 13, margin: "0 0 8px" }}>{inspection}</p>}
      {job.scheduled_start && job.scheduled_end ? (
        <p style={{ fontSize: 13.5, margin: "0 0 8px" }}>
          Scheduled: {job.duration_days} {job.duration_days === 1 ? "day" : "days"}, {formatWeekdayDateOnly(job.scheduled_start)} to {formatWeekdayDateOnly(job.scheduled_end)}
          {crewName ? `, ${crewName}` : ""}. The crew is blocked for the business's opening hours on each of those days.
        </p>
      ) : (
        <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 8px" }}>
          Pick a start day, how many working days it takes and the crew. Days follow your opening hours and skip days you are closed.
        </p>
      )}
      {!readOnly && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Field label="Start day">
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={{ width: 160 }} />
          </Field>
          <Field label="Working days">
            <Input value={days} inputMode="numeric" maxLength={2} onChange={(e) => setDays(e.target.value)} style={{ width: 90 }} />
          </Field>
          <Field label="Crew">
            <Select value={crew} onChange={(e) => setCrew(e.target.value)} style={{ width: 180 }}>
              {crews.length === 0 && <option value="">No staff yet</option>}
              {crews.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Button variant="primary" disabled={busy} onClick={() => void schedule(false)}>
            {job.scheduled_start ? "Move job" : "Schedule job"}
          </Button>
          {job.scheduled_start && (
            <Button disabled={busy} onClick={() => void unschedule()}>
              Take off calendar
            </Button>
          )}
        </div>
      )}
      {conflicts && (
        <div style={{ background: "var(--rc-warning-soft)", border: "1px solid var(--rc-warning)", borderRadius: 6, padding: 12, marginTop: 10, fontSize: 13 }}>
          <strong>{conflicts.length === 1 ? "1 booking is" : `${conflicts.length} bookings are`} already in those days for this crew:</strong>
          <ul style={{ margin: "6px 0 8px", paddingLeft: 18 }}>
            {conflicts.map((c, i) => (
              <li key={i}>
                {formatDateTime(c.starts_at)}: {c.what ?? (c.type === "job" ? "another job" : "booking")}
                {c.who ? ` (${c.who})` : ""}
              </li>
            ))}
          </ul>
          <span>Nothing has been cancelled or changed. Schedule anyway to keep both, or choose different days or another crew.</span>
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <Button variant="primary" disabled={busy} onClick={() => void schedule(true)}>
              Schedule anyway
            </Button>
            <Button onClick={() => setConflicts(null)}>Cancel</Button>
          </div>
        </div>
      )}
      {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "8px 0 0" }}>{error}</p>}
    </div>
  );
}

function JobPanel({ job, readOnly, onClose, onChanged }: { job: JobRow; readOnly: boolean; onClose: () => void; onChanged: () => void }) {
  const { activeBusinessId } = useAuth();
  const [draft, setDraft] = useState<Draft>(draftOf(job));
  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [noteText, setNoteText] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [openCall, setOpenCall] = useState(false);
  const [askedLost, setAskedLost] = useState(false);

  async function loadNotes() {
    const { data } = await supabase.from("job_notes").select("id, kind, body, created_at").eq("job_id", job.id).order("created_at", { ascending: false });
    setNotes((data ?? []) as NoteRow[]);
  }
  useEffect(() => {
    void loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  async function save() {
    setError(null);
    setMessage(null);
    if (!draft.title.trim()) return setError("Give the job a short title.");
    const phone = draftPhone(draft);
    if (!phone.ok) return setError(phone.error);
    const cents = quoteToCents(draft.quote);
    if (cents === undefined) return setError("Enter the quote as a dollar amount.");
    setBusy(true);
    const { error: err } = await supabase.rpc("rpc_portal_update_job", {
      p_job_id: job.id,
      p_fields: {
        title: draft.title,
        description: draft.description,
        urgency: draft.urgency,
        contact_name: draft.contactName,
        contact_phone: phone.e164,
        address_line: draft.addressLine,
        suburb: draft.suburb,
        postcode: draft.postcode,
        quote_amount_cents: cents,
      },
    });
    setBusy(false);
    if (err) return setError(jobErrorMessage(err.message));
    setMessage("Saved.");
    onChanged();
  }

  async function changeStage(stage: string) {
    setError(null);
    setMessage(null);
    if (stage === "lost" && !lostReason.trim() && !askedLost) {
      // The reason is optional, but it is asked for once: choosing Lost again goes ahead without one.
      setAskedLost(true);
      return;
    }
    setBusy(true);
    const { error: err } = await supabase.rpc("rpc_portal_set_job_stage", { p_job_id: job.id, p_stage: stage, p_lost_reason: stage === "lost" ? lostReason : null });
    setBusy(false);
    if (err) return setError(jobErrorMessage(err.message));
    setMessage(null);
    setAskedLost(false);
    onChanged();
    void loadNotes();
  }

  async function addNote(e: FormEvent) {
    e.preventDefault();
    if (!noteText.trim()) return;
    setError(null);
    const { error: err } = await supabase.rpc("rpc_portal_add_job_note", { p_job_id: job.id, p_body: noteText });
    if (err) return setError(jobErrorMessage(err.message));
    setNoteText("");
    void loadNotes();
  }

  return (
    <Modal
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {job.title} <Badge tone={urgencyTone(job.urgency)}>{urgencyLabel(job.urgency)}</Badge>
        </span>
      }
      onClose={onClose}
      width={680}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12, fontSize: 13, color: "var(--rc-text-muted)" }}>
        <span>Added {formatDateTime(job.created_at)}</span>
        <AreaBadge job={job} />
        {job.call_id && (
          <Button variant="ghost" onClick={() => setOpenCall(true)}>
            View call
          </Button>
        )}
      </div>

      <Field label="Stage">
        <Select value={job.stage} disabled={readOnly || busy} onChange={(e) => void changeStage(e.target.value)} style={{ width: 220 }}>
          {JOB_STAGES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
      </Field>
      {job.stage === "lost" && job.lost_reason && <p style={{ fontSize: 13, margin: "6px 0 0" }}>Lost: {job.lost_reason}</p>}
      {!readOnly && (
        <div style={{ margin: "8px 0 12px" }}>
          <Input
            aria-label="Reason if lost"
            placeholder="If lost: why (optional)"
            value={lostReason}
            maxLength={500}
            onChange={(e) => setLostReason(e.target.value)}
            style={{ maxWidth: 360 }}
          />
          {askedLost && <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "4px 0 0" }}>Add a reason above, or choose Lost again to continue without one.</p>}
        </div>
      )}

      <JobFields draft={draft} setDraft={setDraft} disabled={readOnly || busy} showQuote />
      {job.quoted_on && <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "6px 0 0" }}>Quoted {formatQuote(job.quote_amount_cents)} on {job.quoted_on}.</p>}
      {!readOnly && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
          <Button variant="primary" disabled={busy} onClick={() => void save()}>
            Save changes
          </Button>
          {message === "Saved." && <span style={{ fontSize: 12.5, color: "var(--rc-success)" }}>Saved.</span>}
        </div>
      )}
      {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "8px 0 0" }}>{error}</p>}

      {activeBusinessId && <ScheduleSection key={`${job.id}-${job.scheduled_start}-${job.stage}`} job={job} readOnly={readOnly} businessId={activeBusinessId} onChanged={() => { onChanged(); void loadNotes(); }} />}

      <JobPhotos jobId={job.id} contactPhone={job.contact_phone} readOnly={readOnly} />

      <h3 style={{ fontSize: 14, margin: "20px 0 8px" }}>Notes and history</h3>
      {!readOnly && (
        <form onSubmit={addNote} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <Input aria-label="Add a note" placeholder="Add a note" value={noteText} maxLength={2000} onChange={(e) => setNoteText(e.target.value)} style={{ flex: 1 }} />
          <Button type="submit" disabled={!noteText.trim()}>
            Add note
          </Button>
        </form>
      )}
      {notes.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: 0 }}>Nothing yet.</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {notes.map((n) => (
            <li key={n.id} style={{ fontSize: 13, color: n.kind === "event" ? "var(--rc-text-muted)" : "var(--rc-text)" }}>
              <span style={{ color: "var(--rc-text-faint)" }}>{formatDateTime(n.created_at)}</span> {n.body}
            </li>
          ))}
        </ul>
      )}
      {openCall && job.call_id && <CallDetailModal callId={job.call_id} onClose={() => setOpenCall(false)} />}
    </Modal>
  );
}

/**
 * Leads and jobs (migration 0085, docs/leads-and-jobs-spec.md): every enquiry the receptionist took, from the first call to a finished
 * job. Callback requests become jobs automatically while the business has Jobs switched on; the owner can also add one by hand.
 * All changes go through database functions that check the role; a viewer sees everything and changes nothing.
 */
export function Jobs() {
  const { activeBusinessId } = useAuth();
  const readOnly = useReadOnly();
  const [rows, setRows] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"open" | "all" | JobStage>("open");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  async function load() {
    if (!activeBusinessId) return;
    const { data } = await supabase.from("jobs").select(COLUMNS).eq("business_id", activeBusinessId).order("created_at", { ascending: false }).limit(500);
    setRows((data ?? []) as unknown as JobRow[]);
    setLoading(false);
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId]);

  if (loading) return <p className="rc-loading">Loading...</p>;

  const shown = sortJobs(rows.filter((r) => (filter === "all" ? true : filter === "open" ? OPEN_STAGES.includes(r.stage) : r.stage === filter)));
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <div>
      <StickyTitle style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <h1 className="rc-h1" style={{ margin: 0 }}>Jobs</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Select aria-label="Show" value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)} style={{ width: 200 }}>
            <option value="open">Open jobs</option>
            <option value="all">All jobs</option>
            {JOB_STAGES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
          {!readOnly && (
            <Button variant="primary" onClick={() => setAdding(true)}>
              Add job
            </Button>
          )}
        </div>
      </StickyTitle>
      {shown.length === 0 ? (
        <EmptyState>{rows.length === 0 ? "No jobs yet. Callback requests from the receptionist appear here automatically." : "No jobs match this view."}</EmptyState>
      ) : (
        <div className="rc-table-scroll">
          <table className="rc-stack" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
                <th style={{ padding: "10px 12px" }}>Job</th>
                <th style={{ padding: "10px 12px" }}>Contact</th>
                <th style={{ padding: "10px 12px" }}>Where</th>
                <th style={{ padding: "10px 12px" }}>Stage</th>
                <th style={{ padding: "10px 12px" }}>Added</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((job) => (
                <tr key={job.id} onClick={() => setSelectedId(job.id)} style={{ borderBottom: "1px solid var(--rc-border-soft)", verticalAlign: "top", cursor: "pointer" }}>
                  <td data-label="Job" style={{ padding: "10px 12px" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(job.id);
                        }}
                        style={{ background: "none", border: "none", padding: 0, color: "var(--rc-link)", cursor: "pointer", fontSize: 14, textAlign: "left" }}
                      >
                        {job.title}
                      </button>
                      {job.urgency !== "routine" && <Badge tone={urgencyTone(job.urgency)}>{urgencyLabel(job.urgency)}</Badge>}
                    </div>
                  </td>
                  <td data-label="Contact" style={{ padding: "10px 12px" }}>
                    {job.contact_name ?? "Unknown"}
                    {job.contact_phone && <div style={{ color: "var(--rc-text-muted)", fontSize: 13 }}>{formatAuPhoneForDisplay(job.contact_phone)}</div>}
                  </td>
                  <td data-label="Where" style={{ padding: "10px 12px" }}>
                    {[job.suburb, job.postcode].filter(Boolean).join(" ") || "-"}
                    <div>
                      <AreaBadge job={job} />
                    </div>
                  </td>
                  <td data-label="Stage" style={{ padding: "10px 12px" }}>
                    <Badge tone={job.stage === "lost" ? "neutral" : job.stage === "new" ? "warning" : "primary"}>{stageLabel(job.stage)}</Badge>
                  </td>
                  <td data-label="Added" style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>{formatDateTime(job.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && activeBusinessId && <AddJobModal businessId={activeBusinessId} onClose={() => setAdding(false)} onSaved={() => void load()} />}
      {selected && <JobPanel key={selected.id} job={selected} readOnly={readOnly} onClose={() => setSelectedId(null)} onChanged={() => void load()} />}
    </div>
  );
}
